import { useEffect, useMemo } from 'react';
import {
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Tag,
  TreeSelect,
  Typography,
  Skeleton,
} from 'antd';
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  PlusOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { ProductImageEditor } from '@mall/shared/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ApiError, type PageResponse, snapshotToProductImages } from '@mall/shared';
import { useToast } from '../../app/ToastProvider';
import { listBrands } from '../brands/api';
import { listAllCategories } from '../categories/api';
import { categoryDisplayName, type CategoryView } from '../categories/types';
import { uploadAdminFile } from '../uploads/api';
import {
  createProduct,
  getProduct,
  getProductAttributes,
  updateProduct,
} from './api';
import { matchesProductFilters, type ProductFilters } from './matchesFilters';
import type {
  AttributeInput,
  CreateProductRequest,
  ProductSnapshot,
  UpdateProductRequest,
} from './types';

interface FormValues {
  productCode: string;
  name: string;
  nameEn: string;
  categoryId: number | null;
  brandId: number | null;
  priceYuan: number;
  originalPriceYuan: number | null;
  stock: number;
  sortOrder: number | null;
  mainImage: string;
  /** 附图 URL 列表，由上传按钮维护；另有一条隐藏表单项用于注册到 Form。 */
  galleryImageUrls: string[];
  description: string;
  descriptionEn: string;
  attributes: AttributeInput[];
}

const EMPTY_VALUES: FormValues = {
  productCode: '',
  name: '',
  nameEn: '',
  categoryId: null,
  brandId: null,
  priceYuan: 0,
  originalPriceYuan: null,
  stock: 0,
  sortOrder: 0,
  mainImage: '',
  galleryImageUrls: [],
  description: '',
  descriptionEn: '',
  attributes: [],
};

function snapshotToValues(s: ProductSnapshot): FormValues {
  const images = snapshotToProductImages(s);
  return {
    productCode: s.productCode,
    name: s.name,
    nameEn: s.nameEn ?? '',
    categoryId: s.categoryId,
    brandId: s.brandId,
    priceYuan: s.priceCents / 100,
    originalPriceYuan: s.originalPriceCents != null ? s.originalPriceCents / 100 : null,
    stock: s.stock,
    sortOrder: s.sortOrder ?? 0,
    mainImage: images.mainImage,
    galleryImageUrls: images.galleryImageUrls,
    description: s.description ?? '',
    descriptionEn: s.descriptionEn ?? '',
    attributes: s.attributes.map<AttributeInput>((a) => ({
      attrKey: a.attrKey,
      attrKeyEn: a.attrKeyEn ?? null,
      options: (a.options ?? []).map((o) => ({
        zh: o.zh ?? '',
        en: o.en ?? '',
      })),
      sortOrder: a.sortOrder,
    })),
  };
}

interface CategoryTreeOption {
  title: string;
  value: number;
  children: CategoryTreeOption[];
}

function buildCategoryOptions(rows: CategoryView[]): CategoryTreeOption[] {
  const nodes = new Map<number, CategoryTreeOption>();
  for (const row of rows) {
    nodes.set(row.id, { title: categoryDisplayName(row), value: row.id, children: [] });
  }
  const roots: CategoryTreeOption[] = [];
  for (const row of rows) {
    const node = nodes.get(row.id);
    if (!node) continue;
    if (row.parentId && nodes.has(row.parentId)) {
      nodes.get(row.parentId)!.children.push(node);
    } else {
      roots.push(node);
    }
  }
  return roots;
}

function sanitizeAttributes(attrs: AttributeInput[]): AttributeInput[] {
  return attrs
    .map((a) => ({
      attrKey: a.attrKey.trim(),
      attrKeyEn: a.attrKeyEn?.trim() || null,
      options: (a.options ?? [])
        .map((o) => ({
          zh: (o.zh ?? '').trim(),
          en: (o.en ?? '').trim(),
        }))
        .filter((o) => o.zh.length > 0),
      sortOrder: a.sortOrder ?? 0,
    }))
    .filter((a) => a.attrKey.length > 0 && a.options.length > 0);
}

function valuesToCreate(v: FormValues): CreateProductRequest {
  const images = (v.galleryImageUrls ?? []).map((u) => u.trim()).filter(Boolean);
  return {
    productCode: v.productCode.trim(),
    name: v.name.trim(),
    nameEn: v.nameEn.trim() || null,
    categoryId: v.categoryId,
    brandId: v.brandId,
    priceCents: Math.round(v.priceYuan * 100),
    originalPriceCents: v.originalPriceYuan != null ? Math.round(v.originalPriceYuan * 100) : null,
    stock: v.stock,
    mainImage: v.mainImage.trim() || null,
    images,
    description: v.description.trim() || null,
    descriptionEn: v.descriptionEn.trim() || null,
    sortOrder: v.sortOrder ?? 0,
    attributes: sanitizeAttributes(v.attributes ?? []),
  };
}

function valuesToUpdate(v: FormValues): UpdateProductRequest {
  const { productCode: _omit, ...rest } = valuesToCreate(v);
  void _omit;
  return rest;
}

export function ProductFormPage() {
  const params = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  // Return to the list we came from, preserving its page/filters (encoded in the
  // list URL via search params). Fall back to the bare list when this page was
  // opened directly (no in-app history entry to pop back to).
  const goBackToList = () => {
    if (location.key !== 'default') {
      navigate(-1);
    } else if (typeof location.state === 'object' && location.state !== null && 'from' in location.state) {
      navigate(String((location.state as { from: string }).from));
    } else {
      navigate('/products');
    }
  };
  const [form] = Form.useForm<FormValues>();
  const queryClient = useQueryClient();
  const isNew = !params.id || params.id === 'new';
  const productId = isNew ? null : Number(params.id);

  const productQuery = useQuery({
    queryKey: ['admin', 'products', productId],
    queryFn: () => getProduct(productId as number),
    enabled: !!productId,
  });
  const attributesQuery = useQuery({
    queryKey: ['admin', 'products', productId, 'attributes'],
    queryFn: () => getProductAttributes(productId as number),
    enabled: !!productId,
  });

  const brandsQuery = useQuery({ queryKey: ['admin', 'brands'], queryFn: listBrands });
  const categoriesQuery = useQuery({
    queryKey: ['admin', 'categories', 'all'],
    queryFn: listAllCategories,
  });

  const mainImage = Form.useWatch('mainImage', form) ?? '';
  const galleryImageUrls = Form.useWatch('galleryImageUrls', form) ?? [];

  useEffect(() => {
    if (isNew) {
      form.setFieldsValue(EMPTY_VALUES);
      return;
    }
    if (productQuery.data) {
      const initial = snapshotToValues(productQuery.data);
      if (attributesQuery.data && attributesQuery.data.length > 0) {
        initial.attributes = attributesQuery.data.map<AttributeInput>((a) => ({
          attrKey: a.attrKey,
          attrKeyEn: a.attrKeyEn ?? null,
          options: (a.options ?? []).map((o) => ({ zh: o.zh ?? '', en: o.en ?? '' })),
          sortOrder: a.sortOrder,
        }));
      }
      form.setFieldsValue(initial);
    }
  }, [form, isNew, productQuery.data, attributesQuery.data]);

  const saveMutation = useMutation({
    mutationFn: async (values: FormValues) => {
      if (isNew) {
        return createProduct(valuesToCreate(values));
      }
      return updateProduct(productId as number, valuesToUpdate(values));
    },
    onSuccess: async (updated: ProductSnapshot) => {
      if (isNew) {
        await queryClient.invalidateQueries({ queryKey: ['admin', 'products'], refetchType: 'all' });
        toast.success('保存成功');
        goBackToList();
        return;
      }
      // 编辑：原位补丁列表缓存，不 refetch，商品留在原位置
      // 从 location.state.from（列表 URL）解析筛选条件，而非当前编辑页 URL
      const fromUrl = typeof location.state === 'object' && location.state !== null && 'from' in location.state
        ? String((location.state as { from: string }).from)
        : '';
      const fromParams = new URLSearchParams(fromUrl.split('?')[1] ?? '');
      const filters: ProductFilters = {
        keyword: fromParams.get('keyword') ?? undefined,
        categoryId: fromParams.get('category_id') ? Number(fromParams.get('category_id')) : undefined,
        brandId: fromParams.get('brand_id') ? Number(fromParams.get('brand_id')) : undefined,
        status: fromParams.get('status') != null ? Number(fromParams.get('status')) : undefined,
        originalPriceMinYuan: fromParams.get('op_min') != null ? Number(fromParams.get('op_min')) : undefined,
        originalPriceMaxYuan: fromParams.get('op_max') != null ? Number(fromParams.get('op_max')) : undefined,
      };
      let removedByFilter = false;
      queryClient.setQueriesData<PageResponse<ProductSnapshot>>(
        { queryKey: ['admin', 'products'] },
        (old) => {
          if (!old?.list) return old;
          if (!old.list.some((it) => it.id === updated.id)) return old;
          if (matchesProductFilters(updated, filters)) {
            return { ...old, list: old.list.map((it) => (it.id === updated.id ? updated : it)) };
          }
          removedByFilter = true;
          return { ...old, list: old.list.filter((it) => it.id !== updated.id), total: Math.max(0, old.total - 1) };
        },
      );
      queryClient.invalidateQueries({ queryKey: ['admin', 'products', productId], exact: true });
      queryClient.invalidateQueries({ queryKey: ['admin', 'products', productId, 'attributes'], exact: true });
      toast.success('保存成功');
      if (removedByFilter) {
        toast.info('商品已修改，不再符合当前筛选条件，已从列表移除');
      }
      goBackToList();
    },
    onError: (err: Error) => {
      toast.error(err instanceof ApiError ? err.message : '保存失败');
    },
  });

  const brandOptions = useMemo(
    () =>
      (brandsQuery.data ?? []).map((b) => ({
        label: b.name,
        value: b.id,
      })),
    [brandsQuery.data],
  );

  const categoryTreeData = useMemo(
    () => buildCategoryOptions(categoriesQuery.data ?? []),
    [categoriesQuery.data],
  );

  if (!isNew && productQuery.isLoading) {
    return <Skeleton active />;
  }

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={goBackToList}>
          返回列表
        </Button>
        <Typography.Title level={4} style={{ margin: 0 }}>
          {isNew ? '新建商品' : `编辑商品 #${productId}`}
        </Typography.Title>
      </Space>

      <Form<FormValues>
        form={form}
        layout="vertical"
        initialValues={EMPTY_VALUES}
        onFinish={(values) => saveMutation.mutate(values)}
        autoComplete="off"
      >
        <Form.Item name="priceYuan" hidden>
          <InputNumber />
        </Form.Item>
        <Card title="基本信息">
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item
                label="商品编码"
                name="productCode"
                rules={[{ required: true, message: '商品编码必填' }]}
                extra={!isNew ? '编辑时不可修改' : '上线后不可修改'}
              >
                <Input disabled={!isNew} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                label="商品名称（中文）"
                name="name"
                rules={[{ required: true, message: '名称必填' }]}
              >
                <Input maxLength={120} showCount />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="商品名称（英文）" name="nameEn">
                <Input maxLength={120} showCount placeholder="可选" />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="原价（元）" name="originalPriceYuan">
                <InputNumber min={0} step={0.01} precision={2} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item
                label="库存"
                name="stock"
                tooltip="无库存模式：>0 视为可见，=0 视为下架"
              >
                <InputNumber min={0} step={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="排序" name="sortOrder">
                <InputNumber step={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="分类" name="categoryId">
                <TreeSelect
                  allowClear
                  showSearch
                  treeLine
                  treeDefaultExpandAll={false}
                  treeNodeFilterProp="title"
                  loading={categoriesQuery.isLoading}
                  placeholder="选择分类（来自全局类目树）"
                  treeData={categoryTreeData}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="品牌" name="brandId">
                <Select
                  allowClear
                  showSearch
                  loading={brandsQuery.isLoading}
                  placeholder="选择品牌（租户私有）"
                  options={brandOptions}
                  filterOption={(input, opt) =>
                    String(opt?.label ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="描述（中文）" name="description">
                <Input.TextArea rows={4} maxLength={2000} showCount />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="描述（英文）" name="descriptionEn">
                <Input.TextArea rows={4} maxLength={2000} showCount placeholder="可选" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card title="图片" style={{ marginTop: 16 }}>
          <Form.Item name="mainImage" hidden rules={[{ required: true, message: '请至少上传一张封面图' }]}>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item name="galleryImageUrls" hidden>
            <Select mode="tags" open={false} />
          </Form.Item>
          <ProductImageEditor
            value={{
              mainImage: typeof mainImage === 'string' ? mainImage : '',
              galleryImageUrls: Array.isArray(galleryImageUrls) ? galleryImageUrls : [],
            }}
            onChange={(next) => {
              form.setFieldsValue({
                mainImage: next.mainImage,
                galleryImageUrls: next.galleryImageUrls,
              });
            }}
            uploadFile={async (file) => {
              const stored = await uploadAdminFile(file);
              return stored.publicUrl;
            }}
            onError={(message) => toast.error(message)}
          />
        </Card>

        <Card
          title="商品属性（维度 + 多组取值的中英文）"
          style={{ marginTop: 16 }}
          extra="例如「尺寸」对应 L/M/S 各填中文与英文；「颜色」对应 红/绿 与 Red/Green"
        >
          <Form.List name="attributes">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field, index) => (
                  <div
                    key={field.key}
                    style={{
                      border: '1px dashed #d9d9d9',
                      padding: 12,
                      borderRadius: 4,
                      marginBottom: 12,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: 8,
                      }}
                    >
                      <Tag color="blue">属性 #{index + 1}</Tag>
                      <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => remove(field.name)}
                      >
                        移除
                      </Button>
                    </div>
                    <Row gutter={8}>
                      <Col xs={24} md={8}>
                        <Form.Item
                          label="维度名称（中文）"
                          {...field}
                          name={[field.name, 'attrKey']}
                          rules={[{ required: true, message: '必填' }]}
                        >
                          <Input placeholder="例如：尺寸、颜色" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={8}>
                        <Form.Item label="维度名称（英文）" {...field} name={[field.name, 'attrKeyEn']}>
                          <Input placeholder="e.g. Size, Color" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={8}>
                        <Form.Item label="排序" {...field} name={[field.name, 'sortOrder']}>
                          <InputNumber step={1} min={0} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      可选取值（每条 = 中文 + 英文）
                    </Typography.Text>
                    <Form.List name={[field.name, 'options']}>
                      {(optFields, { add: addOpt, remove: removeOpt }) => (
                        <>
                          {optFields.map((optField) => (
                            <Row key={optField.key} gutter={8} align="middle" style={{ marginBottom: 8 }}>
                              <Col xs={24} md={10}>
                                <Form.Item
                                  {...optField}
                                  name={[optField.name, 'zh']}
                                  rules={[{ required: true, message: '中文取值必填' }]}
                                  style={{ marginBottom: 0 }}
                                >
                                  <Input placeholder="中文，如 L、红" />
                                </Form.Item>
                              </Col>
                              <Col xs={24} md={10}>
                                <Form.Item
                                  {...optField}
                                  name={[optField.name, 'en']}
                                  style={{ marginBottom: 0 }}
                                >
                                  <Input placeholder="英文，如 L、Red（可空，空则沿用中文）" />
                                </Form.Item>
                              </Col>
                              <Col xs={24} md={4}>
                                <Button
                                  size="small"
                                  danger
                                  icon={<DeleteOutlined />}
                                  onClick={() => removeOpt(optField.name)}
                                >
                                  删
                                </Button>
                              </Col>
                            </Row>
                          ))}
                          <Button
                            type="dashed"
                            size="small"
                            icon={<PlusOutlined />}
                            onClick={() => addOpt({ zh: '', en: '' })}
                            style={{ marginBottom: 8 }}
                          >
                            添加一条取值
                          </Button>
                        </>
                      )}
                    </Form.List>
                  </div>
                ))}
                <Button
                  block
                  icon={<PlusOutlined />}
                  onClick={() =>
                    add({
                      attrKey: '',
                      attrKeyEn: '',
                      options: [{ zh: '', en: '' }],
                      sortOrder: 0,
                    })
                  }
                >
                  添加属性
                </Button>
              </>
            )}
          </Form.List>
        </Card>

        <Divider />
        <Space>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            htmlType="submit"
            loading={saveMutation.isPending}
            size="large"
          >
            保存
          </Button>
          <Button onClick={goBackToList}>取消</Button>
        </Space>
      </Form>
    </div>
  );
}
